// services/woo.ts
import { WOO_URL, WOO_API_KEY, WOO_API_SECRET } from "../config";
import { UiProduct, WooProduct } from "../types/Product";

const WC_URL = `${WOO_URL}/wp-json/wc/v3`;
const WC_KEY = WOO_API_KEY;
const WC_SECRET = WOO_API_SECRET;

type FetchMode = "summary" | "full";

function toNumber(v: unknown, def = 0) {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : def;
}

// ---- Overloads for great DX ----
export async function getProducts(mode: "summary"): Promise<UiProduct[]>;
export async function getProducts(mode: "full"): Promise<WooProduct[]>;
export async function getProducts(
  mode: FetchMode = "summary"
): Promise<UiProduct[] | WooProduct[]> {
  const authHeader = "Basic " + btoa(`${WC_KEY}:${WC_SECRET}`);

  // Build URL with search params
  const url = new URL(`${WC_URL}/products`);
  url.searchParams.set("per_page", "100");

  // For summary mode, ask WordPress/Woo to only return the fields we need
  // (smaller payloads, faster parsing)
  if (mode === "summary") {
    // WP REST supports the `_fields` whitelist param
    url.searchParams.set(
      "_fields",
      [
        "id",
        "categories",
        "name",
        "slug",
        "permalink",
        "price",
        "regular_price",
        "sale_price",
        "stock_status",
        "stock_quantity",
        "total_sales",
        "images",
        "short_description",
        "type",
        "status",
      ].join(",")
    );
  }

  const res = await fetch(url.toString(), {
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`WooCommerce error ${res.status}: ${text}`);
  }

  const products = await res.json();

  if (mode === "full") {
    // Return as-is but typed to WooProduct[]
    return products as WooProduct[];
  }

  // mode === "summary" → coerce numeric-like fields for UI usage
  // const mapped: UiProduct[] = (products as any[]).map((p) => ({
  //   ...p,
  //   price: toNumber(p.price, 0),
  //   regular_price: toNumber(p.regular_price, 0),
  //   sale_price: toNumber(p.sale_price, 0),
  //   stock_quantity: toNumber(p.stock_quantity, 0),
  //   total_sales: toNumber(p.total_sales, 0),
  // }));

  console.log("Products fetched:", products);

  const mapped: UiProduct[] = (products as any[]).map((p) => ({
    id: p.id,
    name: p.name,
    category: p.categories?.[0]?.name || "Uncategorized",
    price: toNumber(p.price || p.regular_price),
    slug: p.slug,
    stock:
      p.stock_status === "outofstock"
        ? 0
        : typeof p.stock_quantity === "number"
        ? p.stock_quantity
        : 99,
    totalSold: toNumber(p.total_sales),
  }));

  return mapped;
}
